import { useEffect, useMemo, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { Welcome } from './components/Welcome';
import { Sorter } from './components/Sorter';
import { Portrait } from './components/Portrait';
import { ReturnLink } from './components/ReturnLink';
import { activeDeck } from './lib/export';
import type { SortState } from './data/types';
import styles from './App.module.css';

type Phase = 'welcome' | 'sorting' | 'portrait';

const SORT_KEY = 'card-sort:state';
const SETTINGS_KEY = 'card-sort:settings';
const TITLE_KEY = 'card-sort:title';

interface Settings {
  includeAdvanced: boolean;
}

const DEFAULT_SETTINGS: Settings = { includeAdvanced: false };

function initialSortFor(deckIds: string[]): SortState {
  const s: SortState = {};
  for (const id of deckIds) s[id] = 'deck';
  return s;
}

export function App() {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [settings, setSettings] = useState<Settings>(() =>
    storage.get(SETTINGS_KEY, DEFAULT_SETTINGS)
  );
  const deck = useMemo(() => activeDeck(settings.includeAdvanced), [settings.includeAdvanced]);
  const [sort, setSort] = useState<SortState>(() => {
    const saved = storage.get<SortState | null>(SORT_KEY, null);
    return saved ?? initialSortFor(deck.map((c) => c.id));
  });
  const [title, setTitle] = useState<string>(() => storage.get(TITLE_KEY, 'your limits, at a moment in time'));

  // Persist on changes
  useEffect(() => storage.set(SORT_KEY, sort), [sort]);
  useEffect(() => storage.set(SETTINGS_KEY, settings), [settings]);
  useEffect(() => storage.set(TITLE_KEY, title), [title]);

  // When deck toggles, ensure any new cards get added to the deck column and
  // any removed cards drop out of the sort state.
  useEffect(() => {
    setSort((prev) => {
      const next: SortState = {};
      for (const c of deck) {
        next[c.id] = prev[c.id] ?? 'deck';
      }
      return next;
    });
  }, [deck]);

  const sortedCount = deck.filter((c) => sort[c.id] !== 'deck').length;
  const hasInProgress = sortedCount > 0;

  function begin() {
    setSort(initialSortFor(deck.map((c) => c.id)));
    setPhase('sorting');
  }

  function resume() {
    setPhase('sorting');
  }

  function reset() {
    if (confirm('reset every card to the deck?')) {
      setSort(initialSortFor(deck.map((c) => c.id)));
      setPhase('welcome');
    }
  }

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {phase !== 'welcome' && (
          <button
            type="button"
            className={styles.backHome}
            onClick={() => setPhase('welcome')}
          >
            ← welcome
          </button>
        )}
      </header>

      <main className={styles.stage}>
        {phase === 'welcome' && (
          <Welcome
            hasInProgress={hasInProgress}
            includeAdvanced={settings.includeAdvanced}
            onBegin={begin}
            onResume={hasInProgress ? resume : undefined}
            onToggleAdvanced={(v) => setSettings({ ...settings, includeAdvanced: v })}
          />
        )}

        {phase === 'sorting' && (
          <Sorter
            deck={deck}
            sort={sort}
            onSortChange={setSort}
            onFinish={() => setPhase('portrait')}
          />
        )}

        {phase === 'portrait' && (
          <Portrait
            sort={sort}
            deck={deck}
            title={title}
            onTitleChange={setTitle}
            onEdit={() => setPhase('sorting')}
            onReset={reset}
          />
        )}
      </main>

      <footer className={styles.footer}>
        <p>nothing here leaves your browser. there is no right answer.</p>
      </footer>
    </>
  );
}
